import type { FC } from 'react';
import type { PaymentMethodData } from '@/types/stripe';
import { Button } from '@/shared/ui/shadcn/button';
import { Badge } from '@/shared/ui/shadcn/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { CreditCard, Trash2, AlertCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';

interface PaymentMethodCardProps {
  method: PaymentMethodData;
  onDelete?: (method: PaymentMethodData) => void;
  showActions?: boolean;
  canDelete?: boolean;
  deleteBlockedReason?: string;
}

export const PaymentMethodCard: FC<PaymentMethodCardProps> = ({
  method,
  onDelete,
  showActions = true,
  canDelete = true,
  deleteBlockedReason,
}) => {
  const t = useTranslations('paymentMethodCard');

  const renderDeleteButton = () => {
    if (!showActions) return null;

    if (!canDelete && deleteBlockedReason) {
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 cursor-not-allowed"
            >
              <Trash2 className="h-4 w-4 text-muted-foreground/30" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" side="left">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-list-item-value">{t('cannotDelete')}</p>
                <p className="text-list-item-subtitle text-muted-foreground">
                  {deleteBlockedReason}
                </p>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      );
    }

    if (canDelete && onDelete) {
      return (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(method)}
          className="h-8 px-2"
        >
          <Trash2 className="h-4 w-4 text-muted-foreground/50 hover:text-muted-foreground transition-colors" />
        </Button>
      );
    }

    return null;
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3">
        <CreditCard className="w-5 h-5 text-muted-foreground" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-list-item-value">
              {method.card_brand?.toUpperCase() || 'CARD'} ••••{' '}
              {method.card_last_four}
            </span>
            {method.is_expired && (
              <Badge variant="destructive" className="py-0 px-1.5 text-chip h-5">
                {t('expired')}
              </Badge>
            )}
          </div>
          <div className="flex gap-3 mt-1">
            <span className="text-list-item-meta text-muted-foreground">
              {t('expires', {
                month: method.card_exp_month,
                year: method.card_exp_year,
              })}
            </span>
            {method.card_holder_name && (
              <span className="text-list-item-meta text-muted-foreground">
                {method.card_holder_name}
              </span>
            )}
          </div>
        </div>
      </div>

      {renderDeleteButton()}
    </div>
  );
};
